import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { showConfirm } from '../../utils/sweetAlert';
import logger from '../../utils/logger';
import { API, axiosInstance } from './dashboardApi';

export const useLiveSession = () => {
  const [liveSession, setLiveSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [completingSession, setCompletingSession] = useState(false);

  const fetchLiveSession = useCallback(async () => {
    logger.log('🟢 [Dashboard] Fetching live session...');

    try {
      const response = await axiosInstance.get(`${API}/live-session/user`);

      if (response.data.has_session) {
        setLiveSession(response.data.session);
      } else {
        setLiveSession(null);
      }
    } catch (error) {
      logger.error('❌ [Dashboard] Failed to fetch live session:', error);
      setLiveSession(null);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  const handleCancelSessionRequest = async () => {
    if (!liveSession) return;

    const confirmed = await showConfirm(
      'Cancel Session Request?',
      'Are you sure you want to cancel this consultant session request?',
      { confirmButtonText: 'Yes, cancel request' }
    );

    if (!confirmed) return;

    toast.loading('Cancelling request...', { id: 'cancel-request' });

    try {
      const response = await axiosInstance.delete(
        `${API}/live-session/${liveSession.id}/cancel`
      );

      if (response.data.success) {
        setLiveSession(null);
        toast.success('Session request cancelled successfully.', {
          id: 'cancel-request'
        });
        // Refresh session data without page reload
        await fetchLiveSession();
      }
    } catch (error) {
      logger.error('❌ Failed to cancel request:', error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to cancel request. Please try again.';
      toast.error(errorMessage, { id: 'cancel-request' });
    }
  };

  const handleCompleteSession = async () => {
    if (!liveSession || completingSession) return;

    // Validate that user has joined the session
    if (!liveSession.started_at) {
      toast.error(
        'Please join the session first before completing the assessment. Click "Join Live Session" to begin.',
        {
          id: 'complete-session-error',
          duration: 5000,
          position: 'top-center'
        }
      );
      return;
    }

    // Validate that all questions are answered
    const totalQuestions = liveSession.questions_count || 0;
    const answeredCount = liveSession.answered_count || 0;
    
    if (totalQuestions > 0 && answeredCount < totalQuestions) {
      const remaining = totalQuestions - answeredCount;
      toast.error(
        `You must answer all questions before completing the assessment. You have answered ${answeredCount} out of ${totalQuestions} questions. Please answer the remaining ${remaining} question(s) to complete the assessment.`,
        {
          id: 'complete-session-error',
          duration: 6000,
          position: 'top-center'
        }
      );
      return;
    }

    const confirmed = await showConfirm(
      'Complete Assessment?',
      'Are you sure you want to complete this assessment? This action cannot be undone.',
      { confirmButtonText: 'Yes, complete it' }
    );

    if (!confirmed) return;

    setCompletingSession(true);
    toast.loading('Completing assessment...', { id: 'complete-session' });

    try {
      const response = await axiosInstance.post(
        `${API}/live-session/${liveSession.id}/complete`
      );

      if (response.data.success) {
        setLiveSession((prev) => ({
          ...prev,
          status: 'completed'
        }));

        toast.success(
          'Assessment completed successfully! Your results are ready.',
          { id: 'complete-session' }
        );
      }
    } catch (error) {
      logger.error('❌ Failed to complete session:', error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to complete assessment. Please try again.';
      toast.error(errorMessage, { 
        id: 'complete-session',
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setCompletingSession(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Initial fetch
    fetchLiveSession();

    // Poll every 5 seconds for real-time updates when consultant starts session
    const pollInterval = setInterval(() => {
      if (isMounted) {
        fetchLiveSession();
      }
    }, 5000);

    // Listen for custom refresh event (triggered after requesting/canceling session)
    const handleRefresh = () => {
      if (isMounted) {
        fetchLiveSession();
      }
    };
    window.addEventListener('refreshLiveSession', handleRefresh);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
      window.removeEventListener('refreshLiveSession', handleRefresh);
    };
  }, [fetchLiveSession]);

  return {
    liveSession,
    sessionLoading,
    completingSession,
    handleCancelSessionRequest,
    handleCompleteSession
  };
};


